<script setup>
import { ref } from 'vue'
import { httpsCallable } from 'firebase/functions'
import { getFirebaseFunctions } from '@core/firebase/firebase.js'
import { useToast } from '@core/composables/useToast.js'

defineProps({
  open: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'archived'])

const { success } = useToast()

const archiveDeletedCount = ref(0)
const archiveLoading = ref(false)
const archiveError = ref(null)

async function countDeletedUsers() {
  const functions = getFirebaseFunctions()
  const countFn = httpsCallable(functions, 'countDeletedUsers')
  const result = await countFn()
  return result.data.count
}

async function archiveDeletedUsersApi() {
  const functions = getFirebaseFunctions()
  const archiveFn = httpsCallable(functions, 'archiveDeletedUsers')
  const result = await archiveFn()
  return result.data
}

/** Parent calls this when opening the modal to trigger the count */
async function init() {
  archiveError.value = null
  archiveLoading.value = true
  archiveDeletedCount.value = 0

  try {
    const count = await countDeletedUsers()
    archiveDeletedCount.value = count
  } catch (err) {
    archiveError.value = 'שגיאה בספירת משתמשים מחוקים'
    console.error(err)
  } finally {
    archiveLoading.value = false
  }
}

function closeModal() {
  archiveDeletedCount.value = 0
  archiveError.value = null
  emit('close')
}

async function confirmArchiveDeleted() {
  if (archiveDeletedCount.value === 0) {
    closeModal()
    return
  }

  archiveLoading.value = true
  archiveError.value = null

  try {
    const result = await archiveDeletedUsersApi()
    closeModal()
    success(`${result.archivedCount} משתמשים הועברו לארכיון בהצלחה`)
    emit('archived')
  } catch (err) {
    archiveError.value = err.message || 'שגיאה בארכוב משתמשים'
    console.error(err)
  } finally {
    archiveLoading.value = false
  }
}

defineExpose({ init })
</script>

<template>
  <Transition name="modal">
    <div v-if="open" class="modal-overlay" @click.self="closeModal">
      <div class="modal delete-modal">
        <div class="modal-header modal-header-danger">
          <h2 class="modal-title">ארכוב משתמשים מחוקים</h2>
          <button class="modal-close" @click="closeModal">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="archiveLoading && archiveDeletedCount === 0" class="msg msg-info">
            <p>סופר משתמשים מחוקים...</p>
          </div>

          <div v-else-if="archiveError" class="msg msg-error">
            <p>{{ archiveError }}</p>
          </div>

          <div v-else-if="archiveDeletedCount === 0" class="msg msg-info">
            <p>לא נמצאו משתמשים מחוקים לארכוב.</p>
          </div>

          <div v-else class="delete-info">
            <div class="delete-warning-box">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div class="delete-warning-text">
                <p>
                  <strong>נמצאו {{ archiveDeletedCount }} משתמשים מחוקים.</strong>
                </p>
                <p>פעולה זו תעביר אותם לאוסף UsersArchive ותמחק אותם מאוסף Users.</p>
                <p><strong>פעולה זו לא ניתנת לביטול!</strong></p>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" :disabled="archiveLoading" @click="closeModal">ביטול</button>
          <button
            class="btn-delete-confirm"
            :disabled="archiveLoading || archiveDeletedCount === 0"
            @click="confirmArchiveDeleted"
          >
            <svg
              v-if="archiveLoading"
              class="spin"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            <span>{{ archiveLoading ? 'מעביר...' : `ארכב ${archiveDeletedCount} משתמשים` }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg);
}

.delete-modal {
  width: 100%;
  max-width: 440px;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
}

.modal-header-danger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: white;
}

.modal-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin: 0;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: var(--spacing-lg);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
}

.msg {
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  margin-bottom: var(--spacing-md);
}

.msg-error {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.msg-info {
  background: rgba(59, 130, 246, 0.1);
  color: var(--color-primary);
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.delete-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.delete-warning-box {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: var(--border-radius);
  color: var(--color-error);
}

.delete-warning-box svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.delete-warning-text p {
  margin: 0 0 4px 0;
  font-size: var(--font-size-sm);
  line-height: 1.4;
}

.delete-warning-text p:last-child {
  margin-bottom: 0;
}

.btn-cancel {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-cancel:hover {
  background: var(--bg-tertiary);
}

.btn-delete-confirm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--color-error);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-delete-confirm:hover:not(:disabled) {
  background: #b91c1c;
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.btn-delete-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Modal Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .delete-modal,
.modal-leave-active .delete-modal {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .delete-modal,
.modal-leave-to .delete-modal {
  transform: scale(0.95);
}
</style>
